import { useCallback, useEffect, useState } from 'react';
import { Box, Button, InputAdornment, TextField, Typography } from '@mui/material';
import { Add, Search } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { DataTable } from '../../components/tables/DataTable';
import { ConfirmDialog, CONFIRM_INITIAL, type ConfirmDialogState } from '../../components/shared/ConfirmDialog';
import { ROUTES } from '../../constants/routes';
import { getClients, deleteClient } from '../../services/clientService';
import { getProjects } from '../../services/projectService';
import type { Client, Project } from '../../types/project';
import { clientsColumns } from './clientsColumns';
import { NewClientDialog } from './NewClientDialog';
import { EditClientDialog } from './EditClientDialog';
import { usePermissions } from '../../hooks/usePermissions';

interface ClientWithCounts extends Client {
  ProjectCount: number;
}

export const ClientsPage = () => {
  const navigate = useNavigate();
  const { canManageEntities } = usePermissions();
  const [data, setData] = useState<ClientWithCounts[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('ClientName');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [search, setSearch] = useState('');
  const [newOpen, setNewOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [confirmDlg, setConfirmDlg] = useState<ConfirmDialogState>(CONFIRM_INITIAL);

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const [clients, projectResult] = await Promise.all([
        getClients(),
        getProjects({ page: 0, pageSize: 10000 }),
      ]);
      const projects: Project[] = projectResult.data;

      const countsByClient = projects.reduce<Record<string, number>>((acc, p) => {
        acc[p.ClientKey] = (acc[p.ClientKey] || 0) + 1;
        return acc;
      }, {});

      const enriched: ClientWithCounts[] = clients.map((c) => ({
        ...c,
        ProjectCount: countsByClient[c.ClientKey] || 0,
      }));

      setData(enriched);
    } catch {
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
  };

  const handleDeleteClick = async (row: Record<string, unknown>) => {
    const name = row.ClientName as string;
    const key = row.ClientKey as string;
    const count = row.ProjectCount as number;
    if (count > 0) {
      setConfirmDlg({
        open: true,
        title: 'Cannot Delete Client',
        message: `Cannot delete client "${name}" because it has ${count} associated project(s).`,
        confirmLabel: 'OK',
        confirmColor: 'primary',
        onConfirm: () => {},
      });
      return;
    }
    setConfirmDlg({
      open: true,
      title: 'Delete Client',
      message: `Delete client "${name}"? This cannot be undone.`,
      confirmLabel: 'Delete',
      confirmColor: 'error',
      onConfirm: async () => {
        try {
          await deleteClient(key);
          fetchClients();
        } catch (err: unknown) {
          const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
          setConfirmDlg({
            open: true,
            title: 'Error',
            message: msg || 'Failed to delete client.',
            confirmLabel: 'OK',
            confirmColor: 'primary',
            onConfirm: () => {},
          });
        }
      },
    });
  };

  const handleEditClick = (row: Record<string, unknown>) => {
    setEditingClient(row as unknown as Client);
  };

  const handleCreated = () => {
    setNewOpen(false);
    fetchClients();
  };

  const filtered = data.filter((c) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return c.ClientName.toLowerCase().includes(q)
      || (c.ContactPerson || '').toLowerCase().includes(q)
      || (c.Email || '').toLowerCase().includes(q);
  });

  const sortedData = [...filtered].sort((a, b) => {
    const aVal = (a as unknown as Record<string, unknown>)[sortBy];
    const bVal = (b as unknown as Record<string, unknown>)[sortBy];
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return 1;
    if (bVal == null) return -1;
    const cmp = typeof aVal === 'number' && typeof bVal === 'number'
      ? aVal - bVal
      : String(aVal).localeCompare(String(bVal));
    return sortDir === 'asc' ? cmp : -cmp;
  });

  return (
    <Box sx={{ pt: 1, pb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 3 }}>
        <Typography variant="h4" sx={{
          fontWeight: 800, color: 'text.primary', letterSpacing: '-0.025em',
          fontSize: '1.75rem', lineHeight: 1.2,
        }}>
          Clients
        </Typography>
        <Box sx={{ flex: 1 }} />
        <TextField size="small" placeholder="Search clients..."
          value={search} onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment>,
          }}
          sx={{ width: 220, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '0.8125rem' } }}
        />
        {canManageEntities && (
          <Button variant="contained" startIcon={<Add />} onClick={() => setNewOpen(true)}
            size="small" sx={{
              boxShadow: '0 2px 8px rgba(74,103,65,0.2)', borderRadius: '10px',
              textTransform: 'none', fontWeight: 600, px: 2.5, py: 0.75,
              fontSize: '0.8125rem', letterSpacing: '-0.005em',
              transition: 'all 0.2s ease',
              '&:hover': { boxShadow: '0 4px 14px rgba(74,103,65,0.3)', transform: 'translateY(-1px)' },
              '&:active': { transform: 'translateY(0)' },
            }}>
            New Client
          </Button>
        )}
      </Box>

      <DataTable
        columns={clientsColumns}
        data={sortedData as unknown as Record<string, unknown>[]}
        loading={loading}
        onRowClick={(row) => navigate(ROUTES.CLIENT_DETAIL.replace(':clientKey', String(row.ClientKey)))}
        onEditClick={canManageEntities ? handleEditClick : undefined}
        onDeleteClick={canManageEntities ? handleDeleteClick : undefined}
        sortBy={sortBy}
        sortDir={sortDir}
        onSort={handleSort}
      />

      <NewClientDialog open={newOpen} onClose={() => setNewOpen(false)} onCreated={handleCreated} />
      <EditClientDialog
        open={!!editingClient}
        client={editingClient}
        onClose={() => setEditingClient(null)}
        onUpdated={() => { setEditingClient(null); fetchClients(); }}
      />
      <ConfirmDialog state={confirmDlg} onClose={() => setConfirmDlg(CONFIRM_INITIAL)} />
    </Box>
  );
};

export default ClientsPage;
